import React, { useState } from 'react';
import { RegisterRequest, User } from '../types';

interface UserFormProps {
  user?: User;
  onSave: (userData: RegisterRequest | Partial<User> | FormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({ user, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState<RegisterRequest>({
    username: user?.username || '',
    email: user?.email || '',
    password: '',
    display_name: user?.display_name || '',
    department: user?.department || '',
    notes: user?.notes || '',
    hint: user?.hint || '',
    avatar_url: user?.avatar_url || '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.username.trim()) {
      setError('ユーザーIDは必須です');
      return;
    }

    if (!user) {
      // 新規作成の場合
      if (!formData.password.trim()) {
        setError('パスワードは必須です');
        return;
      }

      if (formData.password.length < 6) {
        setError('パスワードは6文字以上で入力してください');
        return;
      }

      if (formData.password !== confirmPassword) {
        setError('パスワードが一致しません');
        return;
      }
    } else {
      // 編集の場合
      if (formData.password && formData.password !== confirmPassword) {
        setError('パスワードが一致しません');
        return;
      }

      if (formData.password && formData.password.length < 6) {
        setError('パスワードは6文字以上で入力してください');
        return;
      }
    }

    try {
      // ファイルが選択されている場合、FormDataとして送信（新規・編集問わず）
      if (avatarFile) {
        const formDataWithFile = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          // ファイルアップロード時はavatar_urlフィールドを除外
          if (key === 'avatar_url') {
            return;
          }
          if (value !== '' && value !== null && value !== undefined) {
            formDataWithFile.append(key, value as string);
          }
        });
        formDataWithFile.append('avatar_file', avatarFile);

        await onSave(formDataWithFile as any);
      } else {
        // 通常の処理
        const saveData = user
          ? {
              username: formData.username || null,
              email: formData.email || null,
              display_name: formData.display_name || null,
              department: formData.department || null,
              notes: formData.notes || null,
              hint: formData.hint || null,
              // Base64データURLの場合は送信しない（プレビューのみ）
              avatar_url: formData.avatar_url && !formData.avatar_url.startsWith('data:') ? formData.avatar_url : null,
              ...(formData.password ? { password: formData.password } : {})
            }
          : {
              // 新規ユーザー作成時も空文字列をnullに変換
              username: formData.username || null,
              email: formData.email || null,
              password: formData.password,
              display_name: formData.display_name || null,
              department: formData.department || null,
              notes: formData.notes || null,
              hint: formData.hint || null,
              avatar_url: formData.avatar_url && !formData.avatar_url.startsWith('data:') ? formData.avatar_url : null
            };

        await onSave(saveData);
      }
    } catch (err: any) {
      setError(err.message || '保存に失敗しました');
    }
  };

  const handleChange = (field: keyof RegisterRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // ファイルサイズチェック (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('ファイルサイズは5MB以下にしてください');
        return;
      }

      // ファイルタイプチェック
      if (!file.type.startsWith('image/')) {
        setError('画像ファイルのみアップロード可能です');
        return;
      }

      setSelectedFile(file);
      setError('');
    }
  };

  const handleUploadAvatar = async () => {
    if (!selectedFile) return;

    // 新規ユーザーの場合は、ファイルを保持してプレビュー表示
    if (!user?.id) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setFormData(prev => ({ ...prev, avatar_url: e.target?.result as string }));
          setAvatarFile(selectedFile);
        }
      };
      reader.readAsDataURL(selectedFile);
      return;
    }

    setUploading(true);
    try {
      // 既存ユーザーの場合も、新規ユーザーと同じ処理にする
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setFormData(prev => ({ ...prev, avatar_url: e.target?.result as string }));
          setAvatarFile(selectedFile); // 既存ユーザーでもファイルを保持
        }
      };
      reader.readAsDataURL(selectedFile);
      setSelectedFile(null);

      // ファイル入力をリセット
      const fileInput = document.getElementById('avatar_file') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error: any) {
      console.error('Avatar upload error:', error);
      setError('アバター画像のアップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '12px',
        width: '450px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
          {user ? 'ユーザー編集' : '新規ユーザー作成'}
        </h3>

        {error && (
          <div style={{
            backgroundColor: '#fee',
            color: '#c33',
            padding: '10px',
            borderRadius: '6px',
            marginBottom: '20px',
            border: '1px solid #fcc'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              ユーザーID *
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => handleChange('username', e.target.value)}
              required
              placeholder="user123"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              メールアドレス
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="user@example.com"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              表示名
            </label>
            <input
              type="text"
              value={formData.display_name}
              onChange={(e) => handleChange('display_name', e.target.value)}
              placeholder="田中太郎"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              部署
            </label>
            <input
              type="text"
              value={formData.department}
              onChange={(e) => handleChange('department', e.target.value)}
              placeholder="営業部"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              ユーザー画像
            </label>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
                画像ファイルをアップロード
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="file"
                  id="avatar_file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  type="button"
                  onClick={handleUploadAvatar}
                  disabled={!selectedFile || uploading}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: !selectedFile || uploading ? '#ccc' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: !selectedFile || uploading ? 'not-allowed' : 'pointer',
                    fontSize: '12px'
                  }}
                >
                  {uploading ? 'アップロード中...' : user ? 'アップロード' : 'プレビュー'}
                </button>
              </div>
              {selectedFile && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  選択されたファイル: {selectedFile.name} ({Math.round(selectedFile.size / 1024)}KB)
                  {!user && ' (プレビュー表示されます)'}
                </div>
              )}
            </div>
            {user && (
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
                  または画像URL
                </label>
                <input
                  type="text"
                  value={formData.avatar_url}
                  onChange={(e) => handleChange('avatar_url', e.target.value)}
                  placeholder="https://example.com/avatar.jpg"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            )}
            {!user && formData.avatar_url && formData.avatar_url.startsWith('data:') && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                📷 画像プレビュー準備完了
              </div>
            )}
            {formData.avatar_url && (
              <div style={{ marginTop: '10px' }}>
                <img
                  src={formData.avatar_url}
                  alt="プレビュー"
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #ddd'
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              パスワード {user ? '(変更する場合のみ入力)' : '*'}
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              required={!user}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '30px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              パスワード確認 {user ? '(パスワードを入力した場合)' : '*'}
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required={!user || !!formData.password}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              パスワードヒント
            </label>
            <input
              type="text"
              value={formData.hint}
              onChange={(e) => handleChange('hint', e.target.value)}
              placeholder="好きな食べ物は？"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '30px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              メモ
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="その他の情報"
              rows={3}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: loading ? '#ccc' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {loading ? '保存中...' : user ? '更新' : '作成'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;